import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { usePermissions } from '../../hooks/usePermissions';
import { useLandingPath } from '../../hooks/useLandingPath';
import { isOrgModule, useOrgModules } from '../../hooks/useOrgModules';
import type { ControlledResource, Intent } from '../../types/profiles';

interface GuardedRouteProps {
  resource: ControlledResource;
  intent?: Intent;
  /**
   * Path to redirect to when permission is denied. Defaults to the effective
   * user's resolved landing page (an accessible page), which keeps guarded
   * pages like /welcome from bouncing into a redirect loop.
   */
  redirectTo?: string;
  children?: React.ReactNode;
}

/** Shown while the access decision waits for the org settings. */
export const AccessCheckPending: React.FC = () => (
  <div className="flex items-center justify-center py-24" role="status" aria-busy="true">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted border-t-primary" />
  </div>
);

/**
 * Route guard based on org modules and profile permissions.
 * Redirects to `redirectTo` when the resource's org module is disabled, or the
 * effective user's profile doesn't grant the requested (resource, intent).
 * Super-admins bypass the permission check, not the module check.
 *
 * Waits for the org's ui_settings before deciding: on a reload they are not
 * loaded yet, the module defaults (Olisé modules off) would deny the page and
 * the user would be bounced to the landing page.
 *
 * Use as a wrapper element around a Route, or with children.
 */
export const GuardedRoute: React.FC<GuardedRouteProps> = ({
  resource,
  intent = 'read',
  redirectTo,
  children,
}) => {
  const { can } = usePermissions();
  const { moduleEnabled, loaded } = useOrgModules();
  const landingPath = useLandingPath();
  if (!loaded) return <AccessCheckPending />;
  const moduleOff = isOrgModule(resource) && !moduleEnabled(resource);
  if (moduleOff || !can(resource, intent)) {
    return <Navigate to={redirectTo ?? landingPath} replace />;
  }
  return <>{children ?? <Outlet />}</>;
};
